import React, { useState } from 'react';
import { useIntl } from 'gatsby-plugin-intl';
import { IoIosAdd, IoIosRemove } from 'react-icons/io';
import { HiOutlineTrash } from 'react-icons/hi';
import { Link } from '@reach/router';
import queryString from 'query-string';
import { useSpring, animated } from 'react-spring';

import { useShopify } from '../../contexts/ShopifyContext';
import { Cart as CartIcon } from '../../svgs/Cart';
import { CartContainer } from './styles';
import { useSpinner } from '../../contexts/SpinnerContext';

const LineItem = ({ id, title, variant, quantity }) => {
  const { toggleSpinner } = useSpinner();
  const { removeFromCart, updateQuantityInCart } = useShopify();
  const { locale, formatMessage } = useIntl();

  const handleRemove = async () => {
    toggleSpinner(true);
    await removeFromCart(id, quantity);
    toggleSpinner(false);
  };
  const handleMinus = async () => {
    if (quantity > 1) {
      toggleSpinner(true);
      await updateQuantityInCart(id, quantity - 1);
      toggleSpinner(false);
    }
  };
  const handlePlus = async () => {
    toggleSpinner(true);
    await updateQuantityInCart(id, quantity + 1);
    toggleSpinner(false);
  };

  return (
    <div className="flex text-black mb-8 md:mb-12 lg:mb-16">
      <div className="w-24 mr-6 ">
        <img src={variant.image.src} className="w-24 border-2 border-blue-900" />
      </div>
      <div className="flex-1 flex flex-col items-start">
        <div className="font-medium mb-2">
          {variant.product && variant.product.handle
            ? formatMessage({ id: `products.${variant.product.handle}.title` })
            : title}
        </div>
        <div
          className={`border border-blue-900 mr-2 px-2 rounded-sm mb-2 text-xs hover:bg-white cursor-pointer bg-blue-100`}>
          {variant.title}
        </div>
        <div className="flex items-center w-full">
          <div className="flex-1 flex items-center">
            <div className="w-24 flex justify-around items-center border border-black rounded py-1 mr-2">
              <IoIosRemove
                size="20"
                className={quantity > 1 ? 'cursor-pointer' : 'text-gray-600 cursor-not-allowed'}
                onClick={handleMinus}
              />
              <span>{quantity}</span>
              <IoIosAdd size="20" className="cursor-pointer" onClick={handlePlus} />
            </div>
            <HiOutlineTrash className="text-red-500 cursor-pointer" size="20" onClick={handleRemove} />
          </div>
          <div className="text-blue-600">
            {Intl.NumberFormat(locale, {
              style: 'currency',
              currency: variant.priceV2.currencyCode,
            }).format(variant.priceV2.amount)}
          </div>
        </div>
      </div>
    </div>
  );
};

export const Cart = () => {
  const { checkout } = useShopify();
  const [cartOpen, setCartOpen] = useState(false);
  const { formatMessage, locale } = useIntl();
  const props = useSpring({
    transform: cartOpen ? 'translate3d(0%,0,0)' : 'translate3d(100%,0,0)',
  });
  const opacity = useSpring({
    opacity: cartOpen ? 1 : 0,
  });

  const openCart = () => {
    document.body.style = 'overflow: hidden;';
    setCartOpen(true);
  };
  const closeCart = () => {
    document.body.style = '';
    setCartOpen(false);
  };
  const openCheckout = () => {
    const { url, query } = queryString.parseUrl(checkout.webUrl);
    const newUrl = {
      url,
      query: {
        ...query,
        locale,
      },
    };
    const checkoutUrl = queryString.stringifyUrl(newUrl);
    window.location = checkoutUrl;
  };

  return (
    <>
      <div className="ml-4 relative cursor-pointer" onClick={openCart}>
        {checkout && checkout.lineItems.length > 0 ? (
          <div
            className="absolute rounded-full bg-red-500 text-white text-center"
            style={{ fontSize: 10, width: 16, height: 16, right: -6, top: -6 }}>
            {checkout && checkout.lineItems.length}
          </div>
        ) : null}
        <CartIcon />
      </div>
      <animated.div
        className={`fixed top-0 left-0 w-screen h-screen flex justify-end`}
        style={{
          visibility: opacity.opacity.interpolate((o) => (o === 0 ? 'hidden' : 'visible')),
        }}>
        <animated.div
          className={`bg-yellow-100 transition duration-500 ease-in-out ${
            cartOpen ? 'bg-opacity-75' : 'bg-opacity-0'
          } absolute w-full h-full top-0 left-0 cursor-pointer`}
          onClick={closeCart}
          style={{
            opacity: 1,
          }}
        />
        <CartContainer
          className="md:border-l-8 md:border-blue-900 bg-white p-6 md:p-12 pb-10 flex flex-col"
          style={props}>
          <div className="flex justify-between items-center w-full mb-8 md:mb-16 lg:mb-24">
            <h2 className="text-4xl font-heading">{formatMessage({ id: 'cart.title' })}</h2>
            <div className="cursor-pointer" onClick={closeCart}>
              <svg width="32" viewBox="0 0 32 32" fill="none">
                <path
                  d="M13.3333 18.6667L16 16M16 16L18.6667 13.3333M16 16L13.3333 13.3333M16 16L18.6667 18.6667M28 16C28 17.5759 27.6896 19.1363 27.0866 20.5922C26.4835 22.0481 25.5996 23.371 24.4853 24.4853C23.371 25.5996 22.0481 26.4835 20.5922 27.0866C19.1363 27.6896 17.5759 28 16 28C14.4241 28 12.8637 27.6896 11.4078 27.0866C9.95189 26.4835 8.62902 25.5996 7.51472 24.4853C6.40042 23.371 5.5165 22.0481 4.91345 20.5922C4.31039 19.1363 4 17.5759 4 16C4 12.8174 5.26428 9.76516 7.51472 7.51472C9.76516 5.26428 12.8174 4 16 4C19.1826 4 22.2348 5.26428 24.4853 7.51472C26.7357 9.76516 28 12.8174 28 16Z"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          {checkout && checkout.lineItems.length > 0 ? (
            <>
              <div className="flex-1 overflow-scroll">
                {checkout.lineItems.map((lineItem) => {
                  return <LineItem key={lineItem.id} {...lineItem} />;
                })}
              </div>
              <div>
                <div className="mb-6">
                  <div className="flex justify-between mb-2">
                    <span className="uppercase text-black">{formatMessage({ id: 'cart.title' })}</span>
                    <span className="text-blue-600">
                      {Intl.NumberFormat(locale, {
                        style: 'currency',
                        currency: checkout.totalPriceV2.currencyCode,
                      }).format(checkout.totalPriceV2.amount)}
                    </span>
                  </div>
                  <p className="text-sm">{formatMessage({ id: 'cart.disclaimer' })}</p>
                </div>
                <button
                  onClick={openCheckout}
                  className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-800 text-white rounded mb-20 lg:mb-20">
                  {formatMessage({ id: 'cart.checkout' })}
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 text-center">
              <div className="w-full font-heading text-xl mb-4">{formatMessage({ id: 'cart.empty' })}</div>
              <Link to={`/${locale}/screens/`} className="text-blue-700">
                {formatMessage({ id: 'cart.findHere' })}
              </Link>
            </div>
          )}
        </CartContainer>
      </animated.div>
    </>
  );
};
